import { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";

/**
 * VideoLightbox
 * - Renders a clickable thumbnail with a poster + play button.
 * - Clicking opens a full-screen modal with a <video> player.
 * - Video is lazy-mounted (only when the modal opens) so the .mp4 is not
 *   downloaded on initial page load.
 * - Closes on Esc, on backdrop click, or via the close button.
 *
 * Props:
 *   src         (required) — video URL
 *   poster      (required) — poster image URL
 *   title       (optional) — short title shown under thumbnail and in modal
 *   description (optional) — small dek under the title
 *   aspect      (optional) — tailwind aspect-* class for the thumbnail
 *   testId      (optional)
 */
export default function VideoLightbox({
  src,
  poster,
  title,
  description,
  aspect = "aspect-video",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    // Try to autoplay (audio off so iOS plays inline silently is fine, but
    // since the user clicked, we can play with sound).
    const v = videoRef.current;
    if (v) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        } catch (_) {}
      }
    };
  }, [open]);

  return (
    <figure data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Play video${title ? `: ${title}` : ""}`}
        className={`group relative w-full ${aspect} overflow-hidden border border-paperline bg-ink/90 cursor-pointer block`}
      >
        <img
          src={poster}
          alt={title || "Video poster"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/10 to-transparent" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-cream/95 backdrop-blur text-burgundy shadow-2xl shadow-ink/30 group-hover:scale-110 transition-transform duration-500">
            <Play size={28} className="ml-1" fill="currentColor" />
            <span className="absolute inset-0 rounded-full border border-cream/60 animate-ping opacity-40" />
          </span>
        </span>
        {title && (
          <span className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-left">
            <span className="block overline text-gold mb-1">Watch</span>
            <span className="heading-display text-cream text-xl md:text-2xl font-medium leading-tight">
              {title}
            </span>
          </span>
        )}
      </button>
      {description && (
        <figcaption className="mt-3 text-sm italic font-serif text-ink/65 leading-relaxed max-w-md">
          {description}
        </figcaption>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-4 sm:p-8 animate-fade-in"
          data-testid="video-lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label="Close video"
            data-testid="video-lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 border border-cream/40 text-cream hover:bg-cream hover:text-ink transition-colors flex items-center justify-center z-10"
          >
            <X size={18} />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl flex flex-col gap-4"
          >
            <video
              ref={videoRef}
              src={src}
              controls
              playsInline
              preload="metadata"
              poster={poster}
              className="w-full max-h-[80vh] bg-ink border border-cream/15"
            />
            {(title || description) && (
              <div className="text-cream/85 text-center max-w-2xl mx-auto">
                {title && (
                  <p className="heading-display text-xl md:text-2xl font-medium">
                    {title}
                  </p>
                )}
                {description && (
                  <p className="mt-1 italic font-serif text-cream/65 text-sm md:text-base">
                    {description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </figure>
  );
}
